import { Component, OnInit, OnDestroy } from '@angular/core';
import { trigger,  style, transition, animate } from '@angular/animations';
import { Subscription } from 'rxjs';
import { BannerNotificationService } from 'src/app/core/services/api/banner-notification.service';
import { EmailVerificationService } from 'src/app/core/services/api/email-verification.service';
import { BannerNotification } from 'src/app/core/models/notification/banner-notification';

@Component({
  selector: 'app-banner-notification',
  templateUrl: './banner-notification.component.html',
  styleUrls: ['./banner-notification.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ 
          opacity: 0, 
          transform: 'translateY(-20px)',
          height: '0px',
          marginBottom: '0px'
        }),
        animate('300ms ease-out', style({ 
          opacity: 1, 
          transform: 'translateY(0)',
          height: '*',
          marginBottom: '*'
        }))
      ]),
      transition(':leave', [
        animate('250ms ease-in', style({ 
          opacity: 0, 
          transform: 'translateY(-20px)',
          height: '0px',
          marginBottom: '0px'
        }))
      ])
    ])
  ]
})
export class BannerNotificationComponent implements OnInit, OnDestroy {
  readonly reduceMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  public notifications: BannerNotification[] = [];
  private subscription: Subscription = new Subscription();

  constructor(private bannerService: BannerNotificationService, private emailVerificationService: EmailVerificationService) { 
    this.notifications = [];
  }

  ngOnInit(): void {
    this.loadBannerNotifications();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadBannerNotifications(): void {
    this.subscription.add(
      this.bannerService.getBannerNotifications().subscribe({
        next: (notifications: BannerNotification[]) => {
          // Filtrer les notifications - si show_banner n'existe pas, on l'affiche par défaut
          this.notifications = notifications.filter(notification => 
            notification.show_banner !== false
          );
        },
        error: (error) => {
          console.error('Error loading banner notifications:', error);
          this.notifications = [];
        }
      })
    );
  }

  getBannerMessage(banner: BannerNotification): string {
    // Si on a un message personnalisé, on l'utilise
    if (banner.label?.default_value) {
      return banner.label.default_value;
    }

    // Sinon, on affiche un message par défaut basé sur banner_type
    switch (banner.banner_type) {
      case 'APPLIED_PROMOTION':
        return 'banner.applied_promotion';
      case 'ACCOUNT_VALIDATION':
        return 'banner.account_validation';
      case 'PAYMENT_METHOD':
      case 'PAYMENT_REQUIRED':
        return 'banner.payment_required';
      case 'TRIAL_MODE':
        return 'banner.trial_mode';
      case 'BETA_VERSION':
        return 'banner.beta_version';
      case 'OWNERSHIP_MISMATCH':
        return 'banner.ownership_mismatch';
      default:
        switch (banner.level) {
          case 'INFO': return 'banner.level_info';
          case 'ERROR': return 'banner.level_error';
          case 'WARNING': return 'banner.level_warning';
          case 'BETA': return 'banner.level_beta';
          default: return 'banner.level_default';
        }
    }
  }

  getBannerMessageParams(banner: BannerNotification): any {
    return banner.params || {};
  }

  shouldShowActionButton(notification: BannerNotification): boolean {
    // Hide action button for promotions and ownership mismatch
    return notification.banner_type !== 'APPLIED_PROMOTION' && 
           notification.banner_type !== 'OWNERSHIP_MISMATCH';
  }
  // Nouvelle méthode pour obtenir le texte du bouton d'action
  getBannerActionText(banner: BannerNotification): string {
    // Si un texte d'action personnalisé est fourni, l'utiliser
    if (banner.action_text) {
      return banner.action_text;
    }

    // Sinon, déterminer le texte basé sur le type de bannière
    switch (banner.banner_type) {
      case 'APPLIED_PROMOTION':
      case 'PAYMENT_METHOD':
      case 'PAYMENT_REQUIRED':
        return 'banner.action_update_payment';
      case 'ACCOUNT_VALIDATION':
        return 'banner.action_verify_account';
      case 'TRIAL_MODE':
        return 'banner.action_upgrade_now';
      case 'BETA_VERSION':
        return 'banner.action_learn_more';
      default:
        return 'banner.action_take_action';
    }
  }

  dismissNotification(notificationId: string): void {
    this.notifications = this.notifications.filter(notification => notification.id !== notificationId);
    // Also remove from service if it was a local notification
    if (notificationId.startsWith('local_')) {
      this.bannerService.removeLocalNotification(notificationId);
    }
  }

  trackByNotificationId(index: number, notification: BannerNotification): string {
    return notification.id || index.toString();
  }

  getBannerClass(level?: string): string {
    switch (level) {
      case 'INFO':
        return 'bo-feedback--info';
      case 'ERROR':
        return 'bo-feedback--error';
      case 'WARNING':
        return 'bo-feedback--warning';
      case 'BETA':
        return 'bo-feedback--beta';
      default:
        return 'bo-feedback--info';
    }
  }

  getBannerIcon(level?: string, type?: string): string {
    if (type === 'ACCOUNT_VALIDATION') return 'bi bi-envelope-check';
    switch (level) {
      case 'INFO':
        return 'bi bi-info-circle';
      case 'ERROR':
        return 'bi bi-exclamation-circle';
      case 'WARNING':
        return 'bi bi-exclamation-triangle';
      case 'BETA':
        return 'bi bi-lightning';
      default:
        return 'bi bi-info-circle';
    }
  }
 
  getBannerTitle(banner: BannerNotification): string {
    const titles = {
      ACCOUNT_VALIDATION: 'banner.title_account_validation',
      PAYMENT_METHOD: 'banner.title_payment_required',
      PAYMENT_REQUIRED: 'banner.title_payment_required',
      TRIAL_MODE: 'banner.title_trial_mode',
      APPLIED_PROMOTION: 'banner.title_applied_promotion',
      OWNERSHIP_MISMATCH: 'banner.title_ownership_mismatch'
    };
    if (titles[banner.banner_type]) return titles[banner.banner_type];
    switch (banner.level) {
      case 'INFO': return 'banner.title_info';
      case 'ERROR': return 'banner.title_error';
      case 'WARNING': return 'banner.title_warning';
      case 'BETA': return 'banner.title_beta';
      default: return 'banner.title_default';
    }
  }
// Méthode pour gérer les clics sur le bouton d'action
  onActionClick(notification: BannerNotification): void {
    // Gérer l'action basée sur le type de notification
    switch (notification.banner_type) {
      case 'APPLIED_PROMOTION':
      case 'PAYMENT_METHOD':
      case 'PAYMENT_REQUIRED':
        // Rediriger vers la page de paiement/facturation
        console.log('Redirecting to payment page');
        // this.router.navigate(['/payment']);
        break;
      case 'ACCOUNT_VALIDATION':
        this.emailVerificationService.resendVerificationEmail();
        this.dismissNotification(notification.id!);
        break;
      case 'TRIAL_MODE':
        // Rediriger vers la page d'upgrade
        console.log('Redirecting to upgrade page');
        // this.router.navigate(['/upgrade']);
        break;
      case 'BETA_VERSION':
        // Rediriger vers la documentation ou page d'infos
        console.log('Redirecting to beta info page');
        // window.open('/beta-info', '_blank');
        break;
      default:
        console.log('Action clicked for:', notification.banner_type);
    }
  }
}
